import { useState } from 'react';
import type { ServicioCatalogo } from '../../api/ops/servicios';
import { Boton } from '../../componentes/Boton';
import { Chip } from '../../componentes/Chip';
import { EstadoCargando, EstadoError, Tarjeta } from '../../componentes/EstadoPagina';
import { Filtros } from '../../componentes/Filtros';
import { Icono } from '../../componentes/Icono';
import { Segmentos } from '../../componentes/Segmentos';
import { Tabla } from '../../componentes/Tabla';
import { rolesDe } from '../../logica/acceso';
import { dinero } from '../../logica/dinero';
import { estadoDeError } from '../../logica/estados';
import { useCabecera } from '../../marco/cabecera';
import { usePermisos } from '../../permisos/permisos';
import type { PropsPantalla } from '../tipos';
import { useDatosServicios, type DatosServicios } from './datos';
import { Consentimientos } from './consentimientos/Consentimientos';
import { FichaServicio } from './FichaServicio';
import { Horarios } from './horarios/Horarios';
import { ImportarCatalogo } from './importar/ImportarCatalogo';
import { Profesionales } from './profesionales/Profesionales';
import { activosQueLoHacen, conDesde, estadoServicio, filtrar, iconoCategoria, lineaServicio, primerNombre, textoDuracion, unir, type FiltroServicios } from './logica';
import { APARTADOS, AYUDA, PERMISOS, esPestana, type Apartado } from './textos';
import './servicios.css';

/**
 * «Servicios y profesionales» de Citas, entrada `servicios` del menú con la ruta `/servicios` (G779): Propietario,
 * Administrador y Encargado (este, por los horarios de 9.7). Barra Servicios · Profesionales · Horarios · Consentimientos
 * (`?apartado=`; Consentimientos solo con `documentos.plantillas.gestionar`).
 *
 * - Servicios (9.4): el catálogo con su ficha (`?servicio={id}&tab=`), «Nuevo servicio» e «Importar catálogo» (9.5,
 *   `?pantalla=importar`, con `citas.catalogo.importar`) en la cabecera; quien solo consulta, la ficha en lectura.
 * - Profesionales (9.6, `profesionales/`), Horarios (9.7, `horarios/`) y Consentimientos (9.11, `consentimientos/`).
 */
export function Servicios(props: PropsPantalla) {
    const permisos = usePermisos();
    // G779: entra quien gestiona el catálogo, los profesionales o confirma cambios de horario; ni Recepción ni el Profesional.
    if (!PERMISOS.entrar.some((p) => permisos.puede(p))) {
        return <SinPermisoServicios {...props} />;
    }
    return <ServiciosConPermiso {...props} />;
}

/** «Tu rol no entra en Servicios y profesionales» (9.4, G779): el Profesional va a Mi agenda; Recepción, a la Agenda. */
function SinPermisoServicios({ membresia, nombreNegocio, navegar, movil }: PropsPantalla) {
    const permisos = usePermisos();
    const agenda = permisos.ambito(PERMISOS.ver);
    const rol = membresia === undefined ? '' : rolesDe(membresia);
    const texto = agenda === 'own'
        ? 'Tu horario y tus citas están en Mi agenda. Si tu negocio te deja cambiar tu horario, lo haces desde allí.'
        : agenda !== null
          ? `Entras como ${rol} en ${nombreNegocio}. Los servicios, el equipo y sus horarios los cambian el propietario, un administrador o el encargado. Las citas del día están en la Agenda.`
          : `Entras como ${rol} en ${nombreNegocio}. Pídeselo al propietario o a un administrador.`;
    return (
        <Tarjeta
            icono="lock"
            baldosa="var(--rv-ink)"
            tinta="#fff"
            ceja="Sin permiso"
            cejaColor="var(--rv-fg-3)"
            titulo="Tu rol no entra en Servicios y profesionales"
            texto={texto}
            botones={agenda === null
                ? <Boton tipo="secundario" icono="house" movil={movil} onClick={() => { navegar('/'); }}>Ir a Inicio</Boton>
                : <Boton tipo="secundario" icono="calendar" movil={movil} onClick={() => { navegar('/citas'); }}>{agenda === 'own' ? 'Ir a Mi agenda' : 'Ir a la Agenda'}</Boton>}
        />
    );
}

function ServiciosConPermiso(props: PropsPantalla) {
    const { ruta, busqueda, navegar, movil } = props;
    const { puede } = usePermisos();
    const apartados = APARTADOS.filter((a) => a.clave !== 'consentimientos' || puede(PERMISOS.plantillas));
    const pedido = busqueda.get('apartado');
    const apartado: Apartado = apartados.find((a) => a.clave === pedido)?.clave ?? 'servicios';
    const ir = (a: Apartado) => {
        navegar(a === 'servicios' ? ruta : `${ruta}?apartado=${a}`);
    };

    return (
        <div className="rv-sv">
            <p className="rv-sv-nota" style={{ marginTop: -8 }}>Lo que ofreces, quién lo hace y cuándo trabaja cada uno.</p>
            <div className="rv-sv-barra">
                <div className="rv-sv-barra__seg">
                    <Segmentos
                        etiqueta="Apartados de Servicios y profesionales"
                        movil={movil}
                        items={apartados.map((a) => ({ etiqueta: a.texto, corta: a.corta, icono: a.icono }))}
                        valor={Math.max(0, apartados.findIndex((a) => a.clave === apartado))}
                        onCambio={(i) => { ir(apartados[i]?.clave ?? 'servicios'); }}
                    />
                </div>
            </div>
            {apartado === 'servicios' ? <ApartadoServicios {...props} /> : apartado === 'profesionales' ? <Profesionales {...props} /> : apartado === 'horarios' ? <Horarios {...props} /> : <Consentimientos {...props} />}
        </div>
    );
}

const FILTROS: readonly { clave: FiltroServicios; texto: string }[] = [
    { clave: 'activos', texto: 'Activos' },
    { clave: 'sin', texto: 'Sin profesional' },
    { clave: 'archivados', texto: 'Archivados' },
];

function ApartadoServicios(props: PropsPantalla) {
    const { ops, negocio, movil, soloLectura, ruta, busqueda, navegar } = props;
    const { puede } = usePermisos();
    const gestiona = puede(PERMISOS.gestionar) && !soloLectura;
    const importa = puede(PERMISOS.importar) && !soloLectura;
    const { datos, error, reintentar, ponerServicio } = useDatosServicios(ops, negocio, puede);
    const importando = busqueda.get('pantalla') === 'importar' && importa;
    const [filtro, setFiltro] = useState<FiltroServicios>('activos');
    const abierto = busqueda.get('servicio');
    const pestana = busqueda.get('tab');
    const abrir = (id: string | null, tab?: string) => {
        navegar(id === null ? ruta : `${ruta}?servicio=${encodeURIComponent(id)}${tab === undefined ? '' : `&tab=${tab}`}`, true);
    };

    useCabecera({
        ayuda: AYUDA.servicios,
        acciones: [
            ...(importa ? [{ clave: 'importar', texto: movil ? 'Importar' : 'Importar catálogo', icono: 'upload' as const, secundaria: true, onClick: () => { navegar(`${ruta}?pantalla=importar`, true); } }] : []),
            ...(gestiona ? [{ clave: 'nuevo', texto: 'Nuevo servicio', icono: 'plus' as const, onClick: () => { abrir('nuevo'); } }] : []),
        ],
    });

    if (error !== null) {
        return <EstadoError seccion="tu catálogo" codigoSoporte={estadoDeError(error).codigoSoporte} onReintentar={reintentar} movil={movil} />;
    }
    if (datos === null) {
        return <EstadoCargando seccion="tu catálogo" />;
    }

    const lista = filtrar(datos.servicios, filtro, '');
    const cifra = (f: FiltroServicios) => filtrar(datos.servicios, f, '').length;
    const servicioAbierto = abierto === null || abierto === 'nuevo' ? null : (datos.servicios.find((s) => s.id === abierto) ?? null);

    return (
        <>
            {!gestiona && (
                <p className="rv-sv-nota">
                    <Icono nombre="eye" />
                    <span>{soloLectura ? 'El negocio está en solo lectura: puedes consultar, pero no cambiar nada.' : 'Con tu rol ves el catálogo. Cambiarlo lo hacen el propietario y los administradores.'}</span>
                </p>
            )}
            {datos.servicios.length === 0 ? (
                <div className="rv-sv-vacia">
                    <span className="rv-sv-baldosa" style={{ background: 'var(--rv-ink)' }}>
                        <Icono nombre="scissors" />
                    </span>
                    <strong>Todavía no tienes servicios</strong>
                    <span>Crea el primero o importa tu catálogo desde otro programa con la plantilla CSV.</span>
                    {(gestiona || importa) && (
                        <div className="rv-sv-vacia__botones">
                            {importa && (
                                <Boton tipo="secundario" icono="upload" movil={movil} onClick={() => { navegar(`${ruta}?pantalla=importar`, true); }}>
                                    Importar catálogo
                                </Boton>
                            )}
                            {gestiona && (
                                <Boton icono="plus" movil={movil} onClick={() => { abrir('nuevo'); }}>
                                    Nuevo servicio
                                </Boton>
                            )}
                        </div>
                    )}
                </div>
            ) : (
                <>
                    <Filtros etiqueta="Estado de los servicios" items={FILTROS.map((f) => ({ etiqueta: f.texto, cifra: cifra(f.clave) }))} valor={Math.max(0, FILTROS.findIndex((f) => f.clave === filtro))} onCambio={(i) => { setFiltro(FILTROS[i]?.clave ?? 'activos'); }} />
                    <TablaServicios datos={datos} lista={lista} filtro={filtro} movil={movil} onAbrir={(id) => { abrir(id); }} />
                </>
            )}
            {(abierto === 'nuevo' && gestiona) || servicioAbierto !== null ? (
                <FichaServicio
                    key={abierto ?? ''}
                    {...props}
                    datos={datos}
                    servicio={servicioAbierto}
                    pestana={esPestana(pestana) ? pestana : 'datos'}
                    gestiona={gestiona}
                    onPestana={(p) => { abrir(servicioAbierto?.id ?? null, p); }}
                    onCerrar={() => { abrir(null); }}
                    onCreado={(s) => {
                        ponerServicio(s);
                        abrir(s.id);
                    }}
                    onCambiado={ponerServicio}
                />
            ) : null}
            <ImportarCatalogo
                ops={ops}
                negocio={negocio}
                movil={movil}
                abierto={importando}
                onCerrar={() => { navegar(ruta, true); }}
                onImportado={reintentar}
                onVerProfesionales={() => { navegar(`${ruta}?apartado=profesionales`); }}
            />
        </>
    );
}

function TablaServicios({ datos, lista, filtro, movil, onAbrir }: { datos: DatosServicios; lista: ServicioCatalogo[]; filtro: FiltroServicios; movil: boolean; onAbrir: (id: string) => void }) {
    const plantilla = (id: string | null | undefined) => (id == null ? '' : (datos.plantillas.find((p) => p.id === id)?.name ?? 'Consentimiento'));
    const vacia = (
        <div className="rv-sv-vacia" style={{ boxShadow: 'none' }}>
            <strong>{filtro === 'archivados' ? 'No hay servicios archivados' : filtro === 'sin' ? 'Todos tus servicios tienen quien los haga' : 'No hay servicios activos'}</strong>
            <span>{filtro === 'sin' ? 'Cada servicio activo lo hace al menos un profesional activo.' : 'Los servicios que crees aparecerán aquí.'}</span>
        </div>
    );
    return (
        <Tabla
            etiqueta="Servicios"
            movil={movil}
            vacia={vacia}
            columnas={[
                { clave: 'servicio', titulo: 'Servicio', ancho: 'minmax(220px, 2fr)' },
                { clave: 'duracion', titulo: 'Duración', ancho: 'minmax(110px, 0.9fr)' },
                { clave: 'precio', titulo: 'Precio', ancho: 'minmax(110px, 0.8fr)', alinear: 'derecha' },
                { clave: 'quien', titulo: 'Quién lo hace', ancho: 'minmax(140px, 1.2fr)' },
                { clave: 'firma', titulo: 'Firma', ancho: 'minmax(130px, 1.1fr)' },
                { clave: 'estado', titulo: 'Estado', ancho: 'minmax(110px, 0.8fr)' },
            ]}
            filas={lista.map((s) => {
                const v = s.current_version;
                const quien = activosQueLoHacen(s, datos.profesionales).map((p) => primerNombre(p.display_name));
                const desde = conDesde(s) ? 'desde ' : '';
                const estado = estadoServicio(s);
                const firma = plantilla(v.consent_template_id);
                const baldosa = (
                    <span className="rv-sv-baldosa" style={{ background: v.color ?? 'var(--rv-ink)' }}>
                        <Icono nombre={iconoCategoria(v.category)} />
                    </span>
                );
                return {
                    clave: s.id,
                    etiqueta: v.name,
                    onAbrir: () => { onAbrir(s.id); },
                    celdas: {
                        servicio: (
                            <span className="rv-sv-celda" style={{ opacity: s.status === 'archived' ? 0.7 : 1 }}>
                                {baldosa}
                                <span className="rv-sv-celda__texto">
                                    <strong>{v.name}</strong>
                                    <small>{lineaServicio(s)}</small>
                                </span>
                            </span>
                        ),
                        duracion: `${desde}${textoDuracion(v.base_duration_minutes)}`,
                        precio: <strong>{`${desde}${dinero(v.base_price)}`}</strong>,
                        quien: quien.length === 0 ? 'Nadie' : unir(quien),
                        firma: (
                            <span className={`rv-sv-firma${firma === '' ? ' rv-sv-firma--no' : ''}`}>
                                <Icono nombre={firma === '' ? 'minus' : 'signature'} />
                                <span>{firma === '' ? 'No pide' : firma}</span>
                            </span>
                        ),
                        estado: estado.texto === 'Activo' ? '' : <Chip estado={estado.chip}>{estado.texto}</Chip>,
                    },
                    tarjeta: (
                        <span className="rv-sv-celda">
                            {baldosa}
                            <span className="rv-sv-celda__texto">
                                <strong>{v.name}</strong>
                                <small>{[textoDuracion(v.base_duration_minutes), dinero(v.base_price), quien.length === 0 ? 'Nadie lo hace' : unir(quien)].join(' · ')}</small>
                                {estado.texto !== 'Activo' && <Chip estado={estado.chip}>{estado.texto}</Chip>}
                            </span>
                        </span>
                    ),
                };
            })}
        />
    );
}
